import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { FaUser } from "react-icons/fa";

const MAX_SUGGESTIONS = 8;

/**
 * Free-text name input with suggestions from existing people. Typing a new name is always allowed;
 * picking a suggestion calls ``onPick`` with the person so callers can fill related fields.
 *
 * @param {object} props
 * @param {string} props.value
 * @param {(value: string) => void} props.onChange
 * @param {(person: {name: string, email?: string, phone?: string, countryCode?: string, company?: string}) => void} [props.onPick]
 * @param {Array<{name: string, email?: string, phone?: string, countryCode?: string, company?: string}>} props.suggestions
 */
export default function NameSuggestInput({
  value,
  onChange,
  onPick,
  suggestions = [],
  disabled = false,
  required = false,
  placeholder,
  ariaLabel
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const boxRef = useRef(null);
  const listId = useId();

  const matches = useMemo(() => {
    const terms = String(value || "").trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!terms.length) return suggestions.slice(0, MAX_SUGGESTIONS);
    const exact = String(value).trim().toLowerCase();
    return suggestions
      .filter((p) => {
        const hay = `${p.name} ${p.email || ""} ${p.company || ""}`.toLowerCase();
        return terms.every((t) => hay.includes(t)) && p.name.toLowerCase() !== exact;
      })
      .slice(0, MAX_SUGGESTIONS);
  }, [suggestions, value]);

  const showList = open && !disabled && matches.length > 0;

  useEffect(() => {
    setActiveIndex(-1);
  }, [value, open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("touchstart", onDocDown);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("touchstart", onDocDown);
    };
  }, [open]);

  const pick = (person) => {
    onChange(person.name);
    onPick?.(person);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!showList) {
      if (e.key === "ArrowDown") setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % matches.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? matches.length - 1 : i - 1));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      pick(matches[activeIndex]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };

  return (
    <div className={`bill-to-combo name-suggest${showList ? " is-open" : ""}`} ref={boxRef}>
      <input
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        required={required}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
      />
      {showList ? (
        <ul className="bill-to-combo__list" role="listbox" id={listId}>
          {matches.map((p, idx) => {
            const sub = [p.company, p.email].filter(Boolean).join(" · ");
            return (
              <li key={`${p.name}|${p.email}`} role="option" aria-selected={idx === activeIndex}>
                <button
                  type="button"
                  className={`bill-to-combo__option${idx === activeIndex ? " is-active" : ""}`}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(p)}
                >
                  <span className="bill-to-avatar bill-to-avatar--sm bill-to-avatar--person">
                    <FaUser aria-hidden="true" />
                  </span>
                  <span className="bill-to-combo__text">
                    <span className="bill-to-combo__title">{p.name}</span>
                    {sub ? <span className="bill-to-combo__sub">{sub}</span> : null}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
